import React from 'react';
import { getFields, prepareIon, getIonValues } from '@buddy-technology/ion-helpers';
import GlobalStyles from '../components/UI/GlobalStyles';
import FormPreviewComponent from '../components/FormPreviewComponent';
import convertDotNoteToDoubleColon from '../utils/convertFieldIDtoRHF';

import {
	CallProvider,
	EventProvider,
	DataProvider,
	ThemeProvider,
} from '../context';

/**
 * FormPreview is a simple composer component that renders individual fields.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} [props.data] - Additional data to be merged with form context and used in calculations
 * @param {Function} [props.eventCallback=() => {}] - Callback function for handling form events
 * @param {object | Object[]} [props.fields] - a field object or array of field objects to preview (required, if not passing ion + fieldIds)
 * @param {Object} props.ion - The raw ION object (required, unless passing fields)
 * @param {object | Object[]} [props.fieldIds] - an array of fieldIds to render for a given ion. (required, if not using fields)
 * @param {string} [props.placesApiKey] - Google Places API key for location services
 * @param {boolean} [props.showValidateButton] - Whether to display validation buttons in the form
 * @param {Object} [props.theme={}] - Theme configuration object for styling
 * @param {string} [props.validationMode] - Form validation mode ('onSubmit' | 'onBlur' | 'onTouched' | 'all')
 *
 * @throws {Error} When ion prop is missing or not a plain object
 * @throws {Error} When viewId prop is missing or not a string
 * @throws {Error} When the specified viewId cannot be found in the ION configuration
 *
 * @returns {React.Component} A complete form preview wrapped with all necessary context providers
 *
 * @example with fields
 * <FormPreview
 *   fields=[{ id: 'field1', type: 'STRING', uiDisplay: {label: 'field one'}}]
 *   data={{ userId: '123' }}
 *   theme={{ primaryColor: '#007bff' }}
 *   eventCallback={(event) => console.log('Form event:', event)}
 *   showValidateButton={true}
 *   validationMode="onTouched"
 * />
 *
 * @example with ion + fieldIds
 * <FormPreview
 *   ion={EXAMPLE_ION}
 *   fields=['field1', 'field2']
 *   data={{ userId: '123' }}
 *   theme={{ primaryColor: '#007bff' }}
 *   eventCallback={(event) => console.log('Form event:', event)}
 *   showValidateButton={true}
 *   validationMode="onTouched"
 * />
 */
const FormPreview = ({
	data,
	eventCallback = () => { },
	fields,
	fieldIds,
	ion: rawIon,
	placesApiKey,
	showValidateButton,
	theme = {},
	validationMode, // onSubmit | onBlur | onTouched | all
}) => {
	let displayedFields = [];
	let ion;
	let defaultFieldValues;

	if (fields) {
		if (typeof fields !== 'object') {
			throw new Error('Fields property must be a field object, or array of field objects.');
		}
		displayedFields = Array.isArray(fields) ? fields : [fields];
		defaultFieldValues = getIonValues(displayedFields, data);
	} else {
		if (!rawIon || typeof rawIon !== 'object' || !fieldIds || !fieldIds.length) {
			const message = 'FieldPreview requires either the field objects (`fields` property), or an ion and list of fieldIds (`ion` and `fieldIds` property) to render.';
			throw new Error(message);
		}
		const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null;
		// passing null here is ok, as it will require the helper to use computer time (as opposed to default of UTC)
		ion = prepareIon(rawIon, { shouldConvertRuleKeys: true, timezone });

		const ionFields = getFields(ion, true);
		defaultFieldValues = getIonValues(ionFields, data);

		// if fieldIds isn't an array, make it one
		const ids = Array.isArray(fieldIds) ? fieldIds : [fieldIds];

		// Now convert our dot-noted ids to use the double-colon format for Joi.
		const allowed = ids.map((id) => convertDotNoteToDoubleColon(id));

		displayedFields = ionFields.filter((field) => (allowed.includes(field.id)));
	}
	// this object holds our incoming props in order to run mingo queries where needed.
	const offeringOptions = {
		data,
	};

	if (!displayedFields.length) {
		// eslint-disable-next-line no-console
		console.warn('FormPreview: no fields found. Please ensure the correct props are being passed.');
		return null;
	}

	return (
		<GlobalStyles theme={theme} useShadow>
			<DataProvider ion={ion || {}} offeringOptions={offeringOptions}>
				<ThemeProvider theme={theme}>
					<EventProvider eventCallback={eventCallback}>
						<CallProvider>
							<FormPreviewComponent
								data={data}
								defaultFieldValues={defaultFieldValues}
								eventCallback={eventCallback}
								fields={displayedFields}
								ion={ion}
								placesApiKey={placesApiKey}
								showValidateButton={showValidateButton}
								validationMode={validationMode}
							/>
						</CallProvider>
					</EventProvider>
				</ThemeProvider>
			</DataProvider>
		</GlobalStyles>
	);
};

export default FormPreview;
